/**
 * Shared props / defaults for `components/icons/*` (TSX icons from `assets/newSvg`).
 *
 * Canonical source artboard: 1024×1024 export · viewBox always `0 0 24 24`.
 * On-screen layout size comes from `size` (`width`/`height` on `<Svg>`).
 * Fill uses `color`, defaulting to theme `text` (app stand-in for CSS `currentColor`).
 */
import type { StyleProp, ViewStyle } from 'react-native';
import { useThemeColors, appTheme } from '../../../shared/theme';

/** Source design export size (documentation only — not used as render size). */
export const APP_ICON_ARTBOARD_PX = 1024 as const;

/** Coordinate system — never deviate. */
export const APP_ICON_VIEW_BOX = '0 0 24 24' as const;

export type AppIconProps = {
  /** On-screen layout size in px. Default: `appTheme.sizes.iconSizeMd` (24). */
  size?: number;
  /**
   * Fill color. Defaults to theme `text` (app equivalent of `currentColor`).
   * Pass an explicit theme token when needed, e.g. `colors.primary`.
   */
  color?: string;
  style?: StyleProp<ViewStyle>;
  accessibilityLabel?: string;
  testID?: string;
};

export type ResolvedAppIconProps = {
  size: number;
  color: string;
  style?: StyleProp<ViewStyle>;
  accessibilityLabel?: string;
  testID?: string;
};

/** Hook: resolve size + fill from theme tokens; keeps icons free of hard-coded palette values. */
export function useAppIconProps(props: AppIconProps): ResolvedAppIconProps {
  const theme_colors = useThemeColors() ?? appTheme.colors;
  const {
    size = appTheme.sizes.iconSizeMd,
    color = theme_colors.text,
    style,
    accessibilityLabel,
    testID,
  } = props;

  return {
    size,
    color,
    style,
    accessibilityLabel,
    testID,
  };
}
